import type {ReactElement} from 'react';
import {useRef} from 'react';
import styles from './drawer.module.scss';
import ClickableDiv from '@Components/clickable-div/clickable-div';
import {noop} from '@Utils/general.util';
import type {DrawerProps} from '@Components/drawer/drawer.types';
import {DrawerSize} from '@Components/drawer/drawer.types';
import {Direction} from '@Components/drawer/drawer.types';

export function Drawer({
  isOpen,
  className = '',
  onClose,
  children,
  overlay = true,
  overlayClassName = '',
  direction = Direction.LEFT,
  size = DrawerSize.MEDIUM,
  hasOverflowHidden = true,
  isAnchoredToParent = false,
}: DrawerProps): ReactElement<DrawerProps> {
  const containerRef = useRef<HTMLDivElement>(null);
  const commonDraweClasses = 'bg-neutral-0 border-s-standard _no-border-top _no-border-left _no-border-bottom';
  const fixedDrawerClasses = '_position-fixed shadow-static-elevation-3';
  const nonFixedDrawerClasses = '_position-relative';

  const getDirectionClasses = (): string => {
    switch (direction) {
      case Direction.LEFT:
        return styles.fromLeft;
      case Direction.RIGHT:
        return styles.fromRight;
      default:
        return styles.fromLeft;
    }
  };

  const getPositionClasses = (): string => {
    if (isAnchoredToParent) {
      return `${styles.anchoredToParent} shadow-static-elevation-3`;
    }

    return overlay ? fixedDrawerClasses : nonFixedDrawerClasses;
  };

  const getSizeClasses = (): string => {
    if (size === DrawerSize.SMALL) {
      return styles.smallDrawer;
    }

    if (size === DrawerSize.LARGE) {
      return styles.largeDrawer;
    }

    return styles.mediumDrawer;
  };

  return (
    <>
      <div
        ref={containerRef}
        className={`
        ${styles.drawerContainer} 
        ${getSizeClasses()}
        ${getDirectionClasses()}
        ${isOpen ? styles.in : ''}
        ${commonDraweClasses}
        ${getPositionClasses()}
        ${hasOverflowHidden ? '_overflow-hidden' : ''}
        ${className}
        `}
      >
        {isOpen ? children : null}
      </div>
      {isOpen && overlay ? <ClickableDiv className={`${styles.overlay} ${isAnchoredToParent ? styles.anchoredToParent : ''} ${overlayClassName}`} onClick={onClose ?? noop} /> : null}
    </>
  );
}
